import { useEffect } from 'react';
import {
  CANVAS_ACTION_EVENT,
  CANVAS_ACTION_RESPONSE_EVENT,
  type AgUiStatePatchEventDetail,
  AG_UI_STATE_PATCH_EVENT,
} from 'agentable-canvas/whiteboard';
import { useCanvasSession } from '../contexts/CanvasSessionContext';
import { executeCanvasHostAction, type CanvasHostAction } from '../lib/canvas-actions-api';

interface CanvasActionEventDetail {
  requestId: string;
  action: string;
  args: Record<string, unknown>;
}

/**
 * Host bridge — executes landing-editor API calls on behalf of agentable
 * tenant tools (generate_site_image, list/read/write site files).
 */
export function useCanvasActionsBridge(): void {
  const { session } = useCanvasSession();
  const siteId = session.siteId;

  useEffect(() => {
    const handler = (event: Event): void => {
      const detail = (event as CustomEvent<CanvasActionEventDetail>).detail;
      if (!detail?.requestId || !detail.action) return;

      void (async () => {
        const result = await executeCanvasHostAction(
          detail.action as CanvasHostAction,
          detail.args ?? {},
          { siteId },
        );
        window.dispatchEvent(
          new CustomEvent(CANVAS_ACTION_RESPONSE_EVENT, {
            detail: { requestId: detail.requestId, result },
            bubbles: true,
            composed: true,
          }),
        );
      })();
    };

    window.addEventListener(CANVAS_ACTION_EVENT, handler);
    return () => window.removeEventListener(CANVAS_ACTION_EVENT, handler);
  }, [siteId]);
}

/** Re-export AG-UI constants for consumers that subscribe to state patches. */
export { AG_UI_STATE_PATCH_EVENT, type AgUiStatePatchEventDetail };
